'use client';

import { Anchor, Card, Grid } from '@mantine/core';
import type { ReactNode } from 'react';
import useSWR from 'swr';

import type { GeolocationDetails } from '@dte/otw-azure-functions/src/common/interfaces/api/Location';
import { formatPhoneNumber } from '@dte/otw-azure-functions/src/common/util/validation/phone/formatPhoneNumber';
import { equalsIgnoreCase } from '@dte/otw-utils-core/src/strings/equalsIgnoreCase';

import { CommonError } from '../../../../../../components/error/CommonError';
import { Spinner } from '../../../../../../components/Spinner/Spinner';
import { type ElectricUtility, ElectricUtilityServiceUrl, getElectricUtilities } from './getElectricUtilities';

const MpscContactUrl = 'https://www.michigan.gov/mpsc/consumer/electricity/electric-utility-addresses-contacts';

function UtilityDetail({ utility }: { utility: ElectricUtility }): ReactNode {
	return (
		<Grid.Col span={{ base: 12, sm: 6 }}>
			<Card bg="gray.1" withBorder={true}>
				<b>{utility.name}</b>
				{utility.phone && <>{formatPhoneNumber(utility.phone)}</>}
				{utility.website && (
					<>
						<Anchor href={utility.website}>{utility.website}</Anchor>
					</>
				)}
				{!utility.website && !utility.phone && (
					<>
						<Anchor href={MpscContactUrl}>Contact Information for {utility.name}</Anchor>
					</>
				)}
			</Card>
		</Grid.Col>
	);
}

export function UtilitySearchResult({
	location,
	premiseId,
	connectionId,
}: {
	location: GeolocationDetails | undefined;
	premiseId?: string;
	connectionId?: string;
}): ReactNode {
	const { data, error, isLoading, mutate } = useSWR<ElectricUtility[], Error>(
		location ? [ElectricUtilityServiceUrl, location.longitude, location.latitude] : null,
		([_url, longitude, latitude]: [string, number, number]) => getElectricUtilities(longitude, latitude),
	);

	if (error) {
		return <CommonError error={error} reset={mutate} />;
	}
	if (isLoading) {
		return <Spinner />;
	}
	if (!location || !data?.length) {
		return (
			<>
				<p>
					{location
						? 'No electric providers were found for this location.'
						: 'The premise location is unavailable for electric utility lookup.'}
				</p>
				<Anchor href={MpscContactUrl}>Contact Information for other electric utilities</Anchor>
			</>
		);
	}

	const otherUtilities = data.filter((utility) => !equalsIgnoreCase(utility.name, 'DTE Electric Company'));
	const hasDte = otherUtilities.length !== data.length;

	return (
		<>
			{hasDte && (
				<>
					<hr />
					<p>DTE is listed as providing electric service near this premise.</p>
					{/* <NearbyActivePremises premiseId={premiseId} connectionId={connectionId} /> */}
				</>
			)}
			{otherUtilities.length > 0 && (
				<>
					<hr />
					<p>
						If the customer is having issues with power, they should contact the utility that provides their
						electricity.
					</p>
					<p>Electric utilities near this premise:</p>
					<Grid>
						{otherUtilities.map((utility) => (
							<UtilityDetail key={utility.id} utility={utility} />
						))}
					</Grid>
				</>
			)}
		</>
	);
}
